/**
 * AdminDrawerButton — botón flotante global (FAB) que abre AdminOnlySidebar
 * como panel lateral deslizable. Aparece SOLO si el usuario es ADMIN.
 *
 * Ventaja: basta con renderizarlo una vez (en UserNav) para que todas las
 * páginas admin tengan acceso consistente al menú, sin tocar cada página.
 */

import React, { useEffect, useState } from 'react';
import AdminOnlySidebar from './admin-only-sidebar';
import { useUser } from '../context/UserContext';

export default function AdminDrawerButton() {
  const { user } = useUser();
  const [open, setOpen] = useState(false);

  // Esc cierra el drawer
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const isAdmin = user?.rol === 'ADMIN' || user?.esAdminComite === true;
  if (!isAdmin) return null;

  return (
    <>
      {/* Botón FAB */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Abrir menú de administrador"
        aria-expanded={open}
        style={{
          position: 'fixed',
          bottom: 24,
          left: 24,
          width: 52,
          height: 52,
          borderRadius: '50%',
          backgroundColor: '#0c2339',
          color: '#fff',
          border: 'none',
          boxShadow: '0 4px 16px rgba(12,35,57,0.4)',
          fontSize: 20,
          cursor: 'pointer',
          zIndex: 1040,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'transform 0.2s, box-shadow 0.2s',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.08)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
      >
        <i className="fa-solid fa-shield-halved" aria-hidden="true"></i>
      </button>

      {/* Drawer overlay */}
      {open && (
        <>
          <div
            onClick={() => setOpen(false)}
            style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(0,0,0,0.5)',
              zIndex: 1050,
              animation: 'ampi-fade-in 0.2s',
            }}
            aria-hidden="true"
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Menú de administrador"
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              bottom: 0,
              width: 320,
              maxWidth: '85vw',
              backgroundColor: '#fff',
              zIndex: 1055,
              overflowY: 'auto',
              boxShadow: '2px 0 16px rgba(0,0,0,0.2)',
              padding: '1rem',
              animation: 'ampi-slide-in 0.25s',
            }}
          >
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Cerrar menú"
              style={{
                position: 'absolute',
                top: 8,
                right: 8,
                background: 'transparent',
                border: 'none',
                fontSize: 24,
                cursor: 'pointer',
                color: '#6c757d',
              }}
            >
              <i className="fa-solid fa-xmark" aria-hidden="true"></i>
            </button>
            {/* Renderizar sidebar en modo "siempre visible" */}
            <AdminOnlySidebar show={true} setShow={() => setOpen(false)} />
          </div>
        </>
      )}

      <style>{`
        @keyframes ampi-fade-in { from { opacity: 0; } to { opacity: 1; } }
        @keyframes ampi-slide-in { from { transform: translateX(-100%); } to { transform: translateX(0); } }
      `}</style>
    </>
  );
}
